/* @typescript-eslint-disable-all */
/* reat-native-web-maps doesn't have types at all */

import { useReactiveVar } from '@apollo/client';
import { useNavigation } from '@react-navigation/native';
import GoogleMapStyle from '@styles/mapStyle.json';
import useIsLoggedIn from 'hooks/useIsLoggedIn';
import React, { useEffect, useState } from 'react';
import { LatLng, Region } from 'react-native-maps';
import { featureCountryCode } from 'states/persistInStorage';
import { useMapStore } from 'stores/mapStore';
import { MapComponentProps, WebAndMobileRegion } from 'types/MapTypes';
import { ParkNavProps } from 'types/navigation';
import {
  defaultRegionByAppLocation,
  getBoxDimensionsFromRegion,
} from 'utils/mapUtils';
import {
  BackButton,
  MapInfoIndicators,
  UserLocationAndFilterButton,
} from './MapButtons';
import MapView from './RNWeb/MapView';

const handleLocationError = (_isAvailable: boolean) => {
  useMapStore.getState().setUserLocation(null);
};

const MapComponent = ({
  children,
  showLocation = true,
  showBeforeMapLoaded = false,
  showBackButton = false,
  onRegionChangeCallback,
  mapStyleWeb,
  initialRegion,
  zonesLoading = false,
}: MapComponentProps) => {
  const appCountry = useReactiveVar(featureCountryCode);
  const { street: showZoomInfo } = useMapStore(
    (state) => state.optimizeMapContent
  );
  const { isLoggedIn } = useIsLoggedIn();
  const defaultRregion = defaultRegionByAppLocation(appCountry, isLoggedIn);
  const [region, setRegion] = useState(defaultRregion);
  let setRegionTimeout: any;
  const navigation = useNavigation<ParkNavProps>();
  const oldRegion = useMapStore((state) => state.regionCoordinates);
  const featureCountry = useReactiveVar(featureCountryCode);
  const isFin = featureCountry === 'FI';
  const handleLocationSuccess = (position: any) => {
    const pos: LatLng = {
      latitude: position.coords.latitude,
      longitude: position.coords.longitude,
    };
    if (pos?.latitude && pos?.longitude) {
      useMapStore.getState().setUserLocation(pos);
    }
  };

  useEffect(() => {
    const newRegion = defaultRegionByAppLocation(appCountry, isLoggedIn);
    setRegion(newRegion as Region);
  }, [appCountry, isLoggedIn]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          handleLocationSuccess(position);
          useMapStore.getState().setMapCoordinates(position.coords);
        },
        () => {
          handleLocationError(true);
        }
      );
    } else {
      // Browser doesn't support Geolocation
      handleLocationError(false);
    }
  }, []);

  useEffect(() => {
    let id: number;
    if (navigator.geolocation) {
      id = navigator.geolocation.watchPosition(
        (position) => handleLocationSuccess(position),
        () => handleLocationError(true),
        {
          enableHighAccuracy: true,
          maximumAge: 5000,
        }
      );
    } else {
      // Browser doesn't support Geolocation
      handleLocationError(false);
    }

    return () => {
      if (id) {
        navigator.geolocation.clearWatch(id);
      }
    };
  }, []);

  const updateRegion = (newRegion: WebAndMobileRegion) => {
    const { zoom = 0 } = newRegion;
    useMapStore
      .getState()
      .setOptimizeMapContent({ street: zoom < 14, other: zoom < 14 });
    clearTimeout(setRegionTimeout);
    setRegionTimeout = setTimeout(() => {
      const { outerBox, zoom: oldZoom } = getBoxDimensionsFromRegion(oldRegion);
      const { viewPort, zoom: newZoom } = getBoxDimensionsFromRegion(newRegion);
      if (
        viewPort.upperRightLat >= outerBox.upperRightLat ||
        viewPort.upperRightLng >= outerBox.upperRightLng ||
        viewPort.bottomLeftLat <= outerBox.bottomLeftLat ||
        viewPort.bottomLeftLng <= outerBox.bottomLeftLng ||
        oldZoom !== newZoom
      ) {
        useMapStore.getState().setRegionCoordinates(newRegion);
      }
    }, 500);
    onRegionChangeCallback?.(newRegion);
  };

  return (
    <>
      <MapView
        showBeforeMapLoaded={showBeforeMapLoaded}
        initialRegion={initialRegion ?? (region as Region)}
        customMapStyle={GoogleMapStyle}
        onRegionChangeComplete={updateRegion}
        mapStyle={mapStyleWeb}
        zonesLoading={zonesLoading}
        testid="web-map-view">
        {children}
      </MapView>
      {showLocation && (
        <UserLocationAndFilterButton testID="find-location-button" />
      )}
      {showBackButton && <BackButton testID="map-back-button" />}
      {
        <MapInfoIndicators
          showZoomedIndicator={isFin && showZoomInfo}
          zonesLoading={zonesLoading}
        />
      }
    </>
  );
};

export default MapComponent;
